import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Table } from "semantic-ui-react";
import { useUpdatePackaging as useUpdateFarmGatePackaging } from "../../../../api/farmGate/packaging";
import { useUpdatePackaging } from "../../../../api/processGate/packaging";

import { TitleView } from "../../../../components";
import { checkValueIsEmpty } from "../../../../utils/utils";
import { renderPackagingRow } from "./PackaginDataView";

const PackagingTable = ({ reviewData, refetch, isEditable }: any) => {
  const [selectPackagingData, setSelectPackagingData] = useState<any>();
  const [editingMeta, setEditingMeta] = useState<{
    dataIndex: number;
    groupKey: string;
    index: number;
  } | null>(null);

  const { isLoading: isLoadingProcessPackaging, mutate: updatePackaging } = useUpdatePackaging();
  const { isLoading: isLoadingUpdateFarmGatePackaging, mutate: updateFarmGatePackaging } =
    useUpdateFarmGatePackaging();

  const packagingDataByProcessGate = useCallback(
    (currentData: any, packageStatus: any) => {
      const result =
        currentData
          ?.map((processPackaging: any) => {
            const packagingList = processPackaging?.packaging || [];

            if (packagingList.length === 0) return null;

            const singleUnitPackaging: any[] = [];
            const bulkUnitPackaging: any[] = [];
            const salesUnitPackaging: any[] = [];
            const transportUnitPackaging: any[] = [];
            const packagingMethod: any[] = [];

            for (let i = 0; i < packagingList.length; i++) {
              const pack = packagingList[i];

              if (Array.isArray(pack.singleUnitPackaging)) {
                singleUnitPackaging.push(...pack.singleUnitPackaging);
              }
              if (Array.isArray(pack.bulkUnitPackaging)) {
                bulkUnitPackaging.push(...pack.bulkUnitPackaging);
              }
              if (Array.isArray(pack.salesUnitPackaging)) {
                salesUnitPackaging.push(...pack.salesUnitPackaging);
              }
              if (Array.isArray(pack.transportUnitPackaging)) {
                transportUnitPackaging.push(...pack.transportUnitPackaging);
              }
              if (Array.isArray(pack.packagingMethod)) {
                packagingMethod.push(...pack.packagingMethod);
              }
            }

            return {
              processGateId: processPackaging._id,
              processGateName: processPackaging[packageStatus],
              gateType: packageStatus === "processGateName" ? "Process Gate" : "Farm Gate",
              singleUnitPackaging,
              bulkUnitPackaging,
              salesUnitPackaging,
              transportUnitPackaging,
              packagingMethod
            };
          })
          .filter(Boolean) || [];

      return result;
    },
    [reviewData]
  );
  const loadPackagingData = useMemo(() => {
    const processPackingData = packagingDataByProcessGate(
      reviewData?.processGate || [],
      "processGateName"
    );
    const farmGatePackingData = packagingDataByProcessGate(
      reviewData?.farmGate || [],
      "farmGateName"
    );
    return [...processPackingData, ...farmGatePackingData];
  }, [reviewData]);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
    setValue
  } = useForm<any>({
    defaultValues: {
      loadPackagingData
    }
  });

  useEffect(() => {
    reset({ loadPackagingData });
  }, [loadPackagingData, reset]);

  function getAllPackagingGroups(data: any) {
    const {
      singleUnitPackaging = [],
      salesUnitPackaging = [],
      bulkUnitPackaging = [],
      transportUnitPackaging = []
    } = data;

    return [
      { label: "Single Unit Packaging", items: singleUnitPackaging },
      { label: "Sales Unit Packaging", items: salesUnitPackaging },
      { label: "Bulk Unit Packaging", items: bulkUnitPackaging },
      { label: "Transport Unit Packaging", items: transportUnitPackaging }
    ];
  }

  function calculateTotalRows(allPackaging: any[]) {
    return allPackaging.reduce((sum, group) => sum + (group.items.length || 1), 0);
  }

  function renderEmptyPackagingRow({
    dataIndex,
    groupIndex,
    currentRow,
    totalRows,
    packName,
    gateType,
    label
  }: any) {
    return (
      <Table.Row key={`${dataIndex}-${groupIndex}-empty`}>
        {currentRow === 0 && (
          <Table.Cell rowSpan={totalRows}>
            {packName} ({gateType})
          </Table.Cell>
        )}
        <Table.Cell>{label}</Table.Cell>
        <Table.Cell colSpan="4" textAlign="center">
          No packaging data available
        </Table.Cell>
      </Table.Row>
    );
  }

  const editData = (item: any, dataIndex: any, groupKey: any, index: any) => {
    setSelectPackagingData(item);
    setEditingMeta({ dataIndex, groupKey, index });
  };

  const cancelEdit = () => {
    reset({ loadPackagingData });
    setSelectPackagingData(null);
  };

  const getCurrentPackagingData = (groupKey: string) => {
    const packagingMap: Record<string, string> = {
      singleUnitPackaging: "single-unit",
      salesUnitPackaging: "sales-unit",
      bulkUnitPackaging: "bulk-unit",
      transportUnitPackaging: "transport-unit"
    };

    return packagingMap[groupKey];
  };

  const onSubmit = (formData: any) => {
    if (!editingMeta) return;

    const { dataIndex, groupKey, index } = editingMeta;

    const updatedRow = formData.loadPackagingData?.[dataIndex]?.[groupKey]?.[index];

    const dataOfPackageDetails = {
      data: updatedRow,
      packagingType: getCurrentPackagingData(groupKey),
      processGateId: loadPackagingData[dataIndex].processGateId,
      packagingId: updatedRow._id,
      gateType: loadPackagingData[dataIndex].gateType
    };

    if (dataOfPackageDetails.gateType === "Process Gate") {
      updatePackaging(dataOfPackageDetails, {
        onSuccess: () => {
          refetch();
          setSelectPackagingData(null);
        }
      });
    } else {
      updateFarmGatePackaging(dataOfPackageDetails, {
        onSuccess: () => {
          refetch();
          setSelectPackagingData(null);
        }
      });
    }
  };

  return (
    <>
      <TitleView CustomTitleViewMain="mt-2" title="Packaging (Process and Farm Gate)" />
      <Table celled className="table" structured>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell className="tableHeader" colSpan="2" textAlign="center">
              Packaging (Process and Farm Gate)
            </Table.HeaderCell>
            <Table.HeaderCell
              className="tableHeader"
              colSpan="14"
              textAlign="center"
              style={{ backgroundColor: "#f4c6db", color: "#000" }}
            >
              Input field
            </Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell className="tableHeaderReview">Gate</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Packaging level</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Package Name</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Packaging material</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Material weight/pk</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Material cost</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {loadPackagingData.map((data, dataIndex) => {
            const packName = data.processGateName;
            const gateType = data.gateType;

            const allPackaging = getAllPackagingGroups(data);
            const totalRows = calculateTotalRows(allPackaging);

            let currentRow = 0;
            const renderedRows: JSX.Element[] = [];

            for (let groupIndex = 0; groupIndex < allPackaging.length; groupIndex++) {
              const group = allPackaging[groupIndex];
              const { label, items } = group;
              const rowCount = items.length || 1;

              if (items.length === 0) {
                renderedRows.push(
                  renderEmptyPackagingRow({
                    dataIndex,
                    groupIndex,
                    currentRow,
                    totalRows,
                    packName,
                    gateType,
                    label
                  })
                );
                currentRow++;
                continue;
              }

              for (let index = 0; index < items.length; index++) {
                const item = items[index];
                const selectPackage = item?._id === selectPackagingData?._id;
                renderedRows.push(
                  renderPackagingRow({
                    dataIndex,
                    groupIndex,
                    index,
                    currentRow,
                    totalRows,
                    rowCount,
                    packName,
                    gateType,
                    label,
                    item,
                    checkValueIsEmpty,
                    editData: (item: any, dataIndex: any, groupKey: any, index: any) =>
                      editData(item, dataIndex, groupKey, index),
                    handleCancel: () => cancelEdit(),
                    selectPackage,
                    register,
                    control,
                    errors,
                    handleSubmit,
                    onSubmit,
                    setEditingMeta,
                    isLoadingProcessPackaging,
                    isLoadingUpdateFarmGatePackaging,
                    isEditable
                  })
                );
                currentRow++;
              }
            }

            return renderedRows;
          })}
        </Table.Body>
      </Table>
    </>
  );
};

export default PackagingTable;
